/**
 * components/hazard/ViolationClassSection.js — ЗӨРЧЛИЙН АНГИЛАЛ, ШИЙТГЭЛ.
 *
 * Лавлахаас (`/incident/reference` → «Ноцтой бус зөрчил») сонгоно:
 *   • Зөрчлийн ТӨРӨЛ   — ямар зөрчил вэ
 *   • ШИЙТГЭЛИЙН төрөл — хариуцлага тооцох хэлбэр
 * Мөн энд байрлах ч ӨӨР сангаас ирдэг:
 *   • Зөрчсөн ЗААЛТ    — ЖУРМЫН САН (`xab.regulation_clause`), ClauseRefPicker
 *
 * ⚠ ЯАГААД СОНГОЛТ ВЭ, ГАРААР БИШ:
 *   Гараар бичсэн текст нь нэг зүйлийг олон хэлбэрээр үүсгэдэг тул
 *   «ямар төрлийн зөрчил хэдэн удаа гарсан» гэдгийг тоолох боломжгүй
 *   болно. Буруу эшилсэн заалт нь албан шаардлага, шийтгэлийн
 *   үндэслэлийг хүчингүй болгож ч болзошгүй.
 *
 * ⚠ ID БА ТЕКСТ ХОЁУЛАА хадгална. `_id` нь холбоос (тоолол, шүүлт),
 *   `_text` нь денормалчилсан хуулбар — лавлах дараа засагдсан ч хуучин
 *   зөрчил тухайн үеийн үг хэллэгээрээ үлдэнэ.
 *
 * ⚠ Лавлах ХООСОН эсвэл бэлэн биш үед хэсэг нь ЧИМЭЭГҮЙ алга болохгүй —
 *   яагаад сонгох юмгүй байгааг ТОДОРХОЙ хэлж, лавлах руу заана.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Select, Spin } from "antd";

import { Card, SectionTitle } from "./common";
import RefLink from "./RefLink";
import ClauseRefPicker from "./ClauseRefPicker";
import { listViolationRefs } from "src/api/hazard";

/**
 * ⚠ «Мэдээллийн ангилал», «Эрсдэлийн түвшин», «Эрсдэлтэй эсэх» гурав ЭНДЭЭС
 *   ГАРСАН — тэдгээр нь зөрчлийн ТАЙЛБАРААС ӨМНӨ асуугддаг тул тусдаа
 *   `InfoClassSection`-д шилжиж, маягтын дээд хэсэгт байрлав.
 *   Талбарын нэр (`info_class_id` …) өөрчлөгдөөгүй тул хадгалалт хэвээр.
 *
 * ⚠ «Зөрчсөн заалт» нь ЭНЭ жагсаалтад алга — тэр нь `violation_reference`
 *   (хоосон лавлах) биш, ЖУРМЫН САН-гаас (`xab.regulation_clause`,
 *   77 269 заалт) сонгогддог болсон. `ClauseRefPicker`-ыг үз.
 */
const FIELDS = [
  {
    kind: "type",
    label: "Зөрчлийн төрөл (дээд тал нь 3)",
    idKey: "violation_type_id",
    textKey: "violation_type_text",
    // ⚠ ОЛОН СОНГОЛТ. Нэг зөрчил хэд хэдэн төрөлд зэрэг хамаардаг («ХХХ
    //   хэрэглээгүй» + «өндөрт ажиллах дүрэм зөрчсөн»). Эхнийх нь ҮНДСЭН
    //   (`_id`/`_text` — тайлан, хэвлэлт хэвээр), бүгд нь `_ids`/`_texts`.
    multi: true,
    idsKey: "violation_type_ids",
    textsKey: "violation_type_texts",
    placeholder: "Ямар төрлийн зөрчил вэ",
  },
  {
    kind: "penalty",
    label: "Шийтгэлийн төрөл",
    idKey: "penalty_id",
    textKey: "penalty_text",
    placeholder: "Хариуцлага тооцох хэлбэр",
  },
];

/** Лавлахын мөрийг сонголтын шошго болгоно. */
const optionLabel = (r) => (r.code ? `${r.code} · ${r.name}` : r.name);

export default function ViolationClassSection({
  value = {},
  onChange,
  /**
   * Мөрийн ЭХНИЙ баганад суух нэмэлт групп (зөрчлийн зураг).
   *
   * ⚠ ЯАГААД ЭНД (2026-11-02, хэрэглэгч): «зураг баримтын хэсгийг нэг
   *   групп болгон яг энэ мөрөнд зөрчлийн ангилал болон шийтгэлийн
   *   төрөлтэй 3 багантай 1 мөр харуулах». Зураг нь өмнө нь
   *   «дэлгэрэнгүй»-н хажууд суудаг байв.
   * ⚠ Өгөөгүй бол мөр 2 багана хэвээр — бусад дуудагч эвдрэхгүй.
   */
  leading = null,
  /**
   * ШИЙТГЭЛИЙН группын ДОР суух агуулга (зөрчлийг арилгах арга
   * хэмжээ) — хэрэглэгч 2026-11-02: «Зөрчлийг арилгах арга
   * хэмжээ-г шийтгэлийн төрөлийн дор оруулах».
   * ⚠ Шийтгэлийн карттай НЭГ торны нүдэнд багцлагдана, эс бөгөөс
   *   `auto-fit` тор үүнийг 4 дэх БАГАНА болгоно.
   */
  trailing = null,
  /**
   * Картын ДЭЭД талд нэмэх агуулга (заавал биш).
   *
   * ⚠ ЯАГААД СУУДАЛ ХЭРЭГТЭЙ ВЭ (2026-09-29): хяналт шалгалтын зөрчилд
   *   ХЭМАБМТ-06-ийн «ноцтой / ноцтой бус» хүндрэл нэмэгдэх ёстой.
   *   Тусдаа карт болговол «Зөрчлийн ангилал» гэсэн ХОЁР гарчигтай
   *   хоёр карт зэрэгцэж, хэрэглэгч «дахин сонгох юм уу» гэж
   *   эргэлздэг байв (2026-09-29-ний ажиглалт). Нэг картад багтаана.
   * ⚠ Өгөөгүй үед юу ч зурахгүй — зөрчлийн бүртгэлийн хуудас
   *   хэвээрээ үлдэнэ.
   */
  extraTop = null,
  /**
   * Нуух талбарууд (`FIELDS[].kind`) — жишээ нь ["type"].
   *
   * ⚠ ЯАГААД (2026-09-29): хяналт шалгалтын зөрчил НОЦТОЙ бол
   *   ХЭМАБМТ-06 Хавсралт №1-ийн ХААЛТТАЙ жагсаалтаас сонгоно. Тэр
   *   үед «Зөрчлийн төрөл» (ноцтой БУСЫН лавлах) зэрэг харагдвал
   *   хоёр өөр ангилал давхарлаж, аль нь үнэн болох нь мэдэгдэхгүй
   *   болно. Хоёул харилцан ҮЛ НИЙЦНЭ.
   * ⚠ Хоосон бол юу ч нуухгүй — зөрчлийн бүртгэлийн хуудас хэвээрээ.
   */
  hideKinds = [],
}) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    // Гурван төрлийг ЗЭРЭГ татна — гурван дараалсан хүсэлт хийвэл
    // маягт нээгдэхэд нүд ирмэх бүрд нэг нэгээр дүүрч самуурна.
    Promise.all(FIELDS.map((f) => listViolationRefs(f.kind)))
      .then((res) => {
        if (!alive) return;
        const next = {};
        let miss = false;
        res.forEach((d, i) => {
          next[FIELDS[i].kind] = d?.items || [];
          if (d?.unavailable) miss = true;
        });
        setData(next);
        setUnavailable(miss);
      })
      .catch(() => {
        if (alive) setUnavailable(true);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const empty = useMemo(
    () => FIELDS.every((f) => !(data[f.kind] || []).length),
    [data],
  );

  const pick = (f) => (v) => {
    const rows = data[f.kind] || [];
    if (f.multi) {
      // 3-аас илүү сонговол сүүлийнхийг хүлээж авахгүй
      const ids = (v || []).slice(0, 3);
      const picked = ids.map((id) => rows.find((r) => r.id === id)).filter(Boolean);
      onChange({
        [f.idsKey]: ids,
        [f.textsKey]: picked.map(optionLabel),
        // Үндсэн = эхнийх
        [f.idKey]: ids[0] ?? null,
        [f.textKey]: picked[0] ? optionLabel(picked[0]) : null,
      });
      return;
    }
    const row = rows.find((r) => r.id === v);
    onChange({
      [f.idKey]: v ?? null,
      // Текстэд КОДЫГ хамт бичнэ — хэвлэсэн бичигт «2.7.5» гэсэн
      // дугаар нь агуулгаас чухал байдаг.
      [f.textKey]: row ? optionLabel(row) : null,
    });
  };

  /** Олон сонголтын одоогийн утга — `_ids` байхгүй хуучин мөрд ганц `_id`. */
  const multiValue = (f) => {
    const ids = value[f.idsKey];
    if (Array.isArray(ids)) return ids;
    if (typeof ids === "string" && ids.trim())
      return ids.split(",").map((x) => Number(x)).filter(Boolean);
    return value[f.idKey] ? [value[f.idKey]] : [];
  };

  /* Нэг сонгогчийн блок — ХОЁР ТУСДАА ГРУПП болсон тул дахин
     хэрэглэнэ (2026-11-02). `hideLabel` нь картын ГАРЧИГ өөрөө
     талбарын нэр болж байгаа үед давхардлыг арилгана. */
  const renderField = (f, hideLabel) => {
    const opts = data[f.kind] || [];
    return (
      <div key={f.kind}>
        {hideLabel ? null : (
          <div style={{ fontSize: 12, color: "#8A94A6", marginBottom: 2 }}>
            {f.label}
          </div>
        )}
        <Select
          mode={f.multi ? "multiple" : undefined}
          maxTagCount={f.multi ? "responsive" : undefined}
          showSearch
          allowClear
          style={{ width: "100%" }}
          placeholder={opts.length ? f.placeholder : "Лавлахад бүртгэгдээгүй"}
          disabled={!opts.length}
          optionFilterProp="label"
          value={f.multi ? multiValue(f) : value[f.idKey] ?? undefined}
          onChange={pick(f)}
          options={opts.map((r) => ({ value: r.id, label: optionLabel(r) }))}
        />
        {/* Хуучин бүртгэлд лавлахгүй текст үлдсэн байж болно —
            сонголт хоосон ч бичигдсэн утгыг НУУХГҮЙ. */}
        {!value[f.idKey] && value[f.textKey] ? (
          <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 2 }}>
            Бүртгэгдсэн: {value[f.textKey]}
          </div>
        ) : null}
      </div>
    );
  };

  const shown = FIELDS.filter((f) => !hideKinds.includes(f.kind));
  const typeField = shown.find((f) => f.kind === "type");
  const penaltyField = shown.find((f) => f.kind === "penalty");

  const refAlert = unavailable ? (
    <Alert
      type="warning"
      showIcon
      style={{ fontSize: 12, marginBottom: 8 }}
      message="Зөрчлийн лавлах бэлэн болоогүй"
      description="create_violation_reference.sql миграцыг ажиллуулна уу. Одоохондоо энэ хэсгийг алгасаж болно."
    />
  ) : !loading && empty ? (
    <Alert
      type="info"
      showIcon
      style={{ fontSize: 12, marginBottom: 8 }}
      message="Лавлах хоосон байна"
      description="Хяналт шалгалтын лавлах → «Ноцтой бус зөрчил» хэсгээс ХАБЭА хэлтэс жагсаалтаа бүртгэнэ."
    />
  ) : null;

  /* ── ХОЁР ТУСДАА ГРУПП, НЭГ МӨРӨНД (2026-11-02, хэрэглэгч) ─────────
     «Зөрчлийн ангилал болон Шийтгэлийн төрөл 2-г салгаж 1 мөрөнд
      2 баганаар харуулах» → эхлээд нэг картын ДОТОР зэрэгцүүлсэн нь
     БУРУУ байв (хэрэглэгч: «тусдаа 2 групп»). Одоо ХОЁР КАРТ.

     ⚠ `auto-fit` + `minmax`: нарийн дэлгэцэд өөрөө нэг багана болно,
       мөн шийтгэлийн групп нуугдсан үед (`hideKinds`) эхнийх нь бүтэн
       өргөнөө эзэлнэ. Хатуу `1fr 1fr` бол хоосон нүх үлдэнэ.
     ⚠ `alignItems: start` — хоёр картын өндөр ялгаатай (зүүн талд
       зөрчсөн заалт нэмэгддэг); эс бөгөөс намхан карт сунаж,
       доторх хоосон зай эвгүй харагдана. */
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
        gap: 12,
        alignItems: "start",
      }}
    >
      {leading || null}
      <Card>
        <SectionTitle right={<RefLink />}>Зөрчлийн ангилал</SectionTitle>
        {extraTop ? <div style={{ marginBottom: 10 }}>{extraTop}</div> : null}
        {refAlert}
        <Spin spinning={loading}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {typeField ? renderField(typeField) : null}
            {/* ── ЗӨРЧСӨН ЗААЛТ — ЖУРМЫН САНГААС ─────────────────────
                Лавлахын хоосон/бэлэн эсэхээс ХАМААРАХГҮЙ: журмын сан
                (1 278 журам · 77 269 заалт) аль хэдийн дүүрсэн.
                ⚠ Заалтын нэр УРТ тул энэ групп дотроо бүтэн өргөнтэй. */}
            <ClauseRefPicker value={value} onChange={onChange} />
          </div>
        </Spin>
      </Card>

      {penaltyField || trailing ? (
        /* ⚠ НЭГ НҮДЭНД багцална: шийтгэл + арга хэмжээ. Тусад нь
           тавивал `auto-fit` тор 4 багана болж, багана бүр нарийсна. */
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {penaltyField ? (
            <Card>
              <SectionTitle>Шийтгэлийн төрөл</SectionTitle>
              <Spin spinning={loading}>
                {/* Гарчиг нь талбарын нэр тул доторх хаягийг НУУНА */}
                {renderField(penaltyField, true)}
              </Spin>
            </Card>
          ) : null}
          {trailing || null}
        </div>
      ) : null}
    </div>
  );
}
